import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import courseData from '../data/course.json';
import salesforceDevImage from '../images/copado-course.png';
import salesforceAiImage from '../images/salesforce-ai.png';
import khyatiMehtaImage from '../images/journey/7.jpeg';
import './CourseDetail.css';
import amitSinghImage from '../images/amit-singh.png';

const KONFHUB_SCRIPT_URL = 'https://widget.konfhub.com/widget.js';
const KONFHUB_BUTTON_IDS = {
  // Backward compatibility: old slug should enroll to AI program
  'salesforce-developer': 'btn_f9b8c7a58b83',
  'salesforce-ai-developer': 'btn_f9b8c7a58b83',
  'salesforce-devops-copado-agentia': 'btn_483b56d18976',
};

const instructorImages = {
  'Khyati Mehta': khyatiMehtaImage,
  'Amit Singh': amitSinghImage,
};

const StarRating = ({ rating, reviewsCount }) => {
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const empty = 5 - full - (hasHalf ? 1 : 0);
  return (
    <div className="course-detail-rating">
      {Array.from({ length: full }, (_, i) => (
        <span key={'f-' + i} className="star star-full" aria-hidden="true">★</span>
      ))}
      {hasHalf && <span className="star star-half" aria-hidden="true">★</span>}
      {Array.from({ length: empty }, (_, i) => (
        <span key={'e-' + i} className="star star-empty" aria-hidden="true">☆</span>
      ))}
      {reviewsCount != null && (
        <span className="course-detail-reviews">({reviewsCount} reviews)</span>
      )}
    </div>
  );
};

const StatRow = ({ icon, label, value }) => (
  <div className="course-detail-stat-row">
    <span className="course-detail-stat-icon" aria-hidden="true">{icon}</span>
    <span className="course-detail-stat-value">{value}</span>
    <span className="course-detail-stat-label">{label}</span>
  </div>
);

const CertificateIcon = ({ name }) => {
  const icons = { official: '🚀', shareable: '🔗', motivator: '🏆', mission: '🎯' };
  return <span className="cert-icon" aria-hidden="true">{icons[name] || '✓'}</span>;
};

const CourseDetail = () => {
  const { courseSlug } = useParams();
  const location = useLocation();
  const courses = courseData.courses || {};
  const course = courses[courseSlug];
  const [emailOptIn, setEmailOptIn] = useState(false);
  const scriptContainerRef = useRef(null);

  const konfhubButtonId =
    KONFHUB_BUTTON_IDS[courseSlug] || KONFHUB_BUTTON_IDS['salesforce-developer'];

  // Load KonfHub script into a hidden container once per course (so each course can have its own button id)
  useEffect(() => {
    const container = scriptContainerRef.current;
    if (!container || !konfhubButtonId) return;

    const script = document.createElement('script');
    script.src = KONFHUB_SCRIPT_URL;
    script.async = true;
    script.setAttribute('button_id', konfhubButtonId);
    container.appendChild(script);

    return () => {
      if (script.parentNode === container) {
        container.removeChild(script);
      }
    };
  }, [konfhubButtonId]);

  // Click the first KonfHub-generated button so payment popup opens from our Enroll button
  const handleKonfhubClick = useCallback((e) => {
    e.preventDefault();
    const container = scriptContainerRef.current;
    if (!container) return;

    const buttons = container.querySelectorAll('button');
    const firstButton = buttons[0];

    if (firstButton) {
      firstButton.click();
    } else {
      // eslint-disable-next-line no-console
      console.warn('KonfHub button not found');
    }
  }, []);

  if (!course) {
    return (
      <PageLayout>
        <section className="page-hero">
          <h1>Course not found</h1>
          <p>This course may have been moved or does not exist.</p>
          <Link to="/courses">Browse offerings</Link>
        </section>
      </PageLayout>
    );
  }

  const related = (course.relatedCourseSlugs || [])
    .map((slug) => courses[slug])
    .filter(Boolean);

  const heroImage =
    courseSlug === 'salesforce-devops-copado-agentia'
      ? salesforceDevImage
      : courseSlug === 'salesforce-ai-developer' ||
        courseSlug === 'salesforce-developer' ||
        courseSlug === 'salesforce-lwc-development'
        ? salesforceAiImage
        : course.imageUrl;

  const datePacingText =
    course.enrollDate === course.pacing
      ? course.pacing
      : `${course.enrollDate || ''}${course.enrollDate && course.pacing ? ' · ' : ''}${course.pacing || ''}`.trim() || '—';

  const searchParams = new URLSearchParams(location.search || '');
  const selectedDate = searchParams.get('date');
  const selectedTime = searchParams.get('time');
  const allUpcomingBatches =
    course.upcomingBatches?.batches?.length > 0
      ? course.upcomingBatches.batches
      : course.upcomingBatches?.date
        ? [{ date: course.upcomingBatches.date, time: course.upcomingBatches.time }]
        : [];
  const normalizeBatchValue = (value) => String(value || '').replace(/\+/g, ' ').trim();
  const selectedUpcomingBatch = (() => {
    if (allUpcomingBatches.length === 0) return null;
    if (selectedDate || selectedTime) {
      const match = allUpcomingBatches.find(
        (batch) =>
          (!selectedDate || normalizeBatchValue(batch.date) === normalizeBatchValue(selectedDate)) &&
          (!selectedTime || normalizeBatchValue(batch.time) === normalizeBatchValue(selectedTime))
      );
      return match || allUpcomingBatches[0];
    }
    return allUpcomingBatches[0];
  })();
  const batchesToShow =
    allUpcomingBatches.length > 0 ? allUpcomingBatches : selectedUpcomingBatch ? [selectedUpcomingBatch] : [];

  return (
    <PageLayout>
      <div className="course-detail">
        <div className="course-detail-layout">
          <div className="course-detail-main">
            <div
              ref={scriptContainerRef}
              aria-hidden="true"
              style={{
                width: '0px',
                height: '0px',
                overflow: 'hidden',
              }}
            />
            <header className="course-detail-hero">
              <div className="course-detail-hero-image-wrap">
                <img src={heroImage} alt={course.title} loading="lazy" />
              </div>
              <div className="course-detail-hero-text">
                <h1 className="course-detail-title">{course.title}</h1>
                <p className="course-detail-provider">by {course.provider}</p>
                <p className="course-detail-summary">{course.summary}</p>
                <StarRating rating={course.rating} reviewsCount={course.reviewsCount} />
              </div>
            </header>

            <section className="course-detail-block">
              <h2 className="course-detail-block-title">About this course</h2>
              <p className="course-detail-about">{course.about}</p>
            </section>

            <section className="course-detail-block">
              <h2 className="course-detail-block-title">What you'll learn</h2>
              <ul className="course-detail-learn-list">
                {(course.whatYouLearn || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="course-detail-block">
              <h2 className="course-detail-block-title">Instructors</h2>
              <div className="course-detail-instructors">
                {(course.instructors || []).map((inst, i) => (
                  <div key={i} className="course-detail-instructor">
                    <div className="course-detail-instructor-avatar">
  <img
    src={instructorImages[inst.name] || inst.imageUrl}
    alt={inst.name}
    loading="lazy"
  />
</div>
                    <div className="course-detail-instructor-info">
                      <strong>{inst.name}</strong>
                      <span>{inst.title}</span>
                      <span>{inst.affiliation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {course.certificate && (
              <section className="course-detail-block">
                <h2 className="course-detail-block-title">{course.certificate.title}</h2>
                <div className="course-detail-cert-benefits">
                  {(course.certificate.benefits || []).map((benefit, i) => (
                    <div key={i} className="course-detail-cert-item">
                      <CertificateIcon name={benefit.icon} />
                      <div>
                        <strong>{benefit.title}</strong>
                        <p>{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Hide enroll button for Web Development and Full Stack Web Development for now */}
            {courseSlug !== 'web-development' && courseSlug !== 'full-stack-web-development' && (
              <section className="course-detail-enroll-bottom">
                {/* <p className="course-detail-meta-inline">Select batch date on checkout</p> */}
                <button
                  type="button"
                  className="course-detail-enroll-btn"
                  onClick={handleKonfhubClick}
                > 
                  Enroll Now
                </button>
                {/* <label className="course-detail-email-optin">
                  <input
                    type="checkbox"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                  />
                  I would like to receive email from {course.provider} and learn about other offerings.
                </label> */}
              </section>
            )}
          </div>

          <aside className="course-detail-sidebar">
          {batchesToShow.length > 0 && (
            <div className="course-detail-upcoming-card">
              <h3 className="course-detail-upcoming-title">{course.upcomingBatches?.title || 'Upcoming batches'}</h3>
              <div className="course-detail-upcoming-list">
                {batchesToShow.map((batch, index) => (
                  <div
                    key={`${batch.date}-${batch.time}-${index}`}
                    className={`course-detail-upcoming-row${
                      selectedUpcomingBatch &&
                      batch.date === selectedUpcomingBatch.date &&
                      batch.time === selectedUpcomingBatch.time
                        ? ' course-detail-upcoming-row--active'
                        : ''
                    }`}
                  >
                    <div className="course-detail-upcoming-item">
                      <span className="course-detail-upcoming-label">Date</span>
                      <span className="course-detail-upcoming-value">{batch.date || '—'}</span>
                    </div>
                    <div className="course-detail-upcoming-item">
                      <span className="course-detail-upcoming-label">Time</span>
                      <span className="course-detail-upcoming-value">{batch.time || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="course-detail-stats-card">
              <StatRow icon="👥" label="learners" value={course.stats && course.stats.learners ? course.stats.learners : '—'} />
              <StatRow icon="₹" label="price" value={course.stats && course.stats.price ? String(course.stats.price).replace(/^₹\s*/, '') : '—'} />
              <StatRow icon="⏰" label="duration" value={course.stats && course.stats.duration ? course.stats.duration : '—'} />
              <StatRow icon="📊" label="level" value={course.stats && course.stats.level ? course.stats.level : '—'} />
              <StatRow icon="📍" label="location" value={course.stats && course.stats.location ? course.stats.location : '—'} />
            </div>

            {/* Hide sidebar enroll button for Web Development and Full Stack Web Development for now */}
            {courseSlug !== 'web-development' && courseSlug !== 'full-stack-web-development' && (
              <div className="course-detail-sidebar-card">
                {/* <p className="course-detail-meta-inline">Select batch date on checkout</p> */}
                <button
                  type="button"
                  className="course-detail-enroll-btn"
                  onClick={handleKonfhubClick}
                >
                  Enroll Now
                </button>
                {/* <label className="course-detail-email-optin">
                  <input
                    type="checkbox"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                  />
                  I would like to receive email from {course.provider} and learn about other offerings related to {course.title}.
                </label> */}
              </div>
            )}

            <div className="course-detail-contact-card">
              <h3 className="course-detail-contact-title">Have a query?</h3>
              <p className="course-detail-contact-text">If you have any query, please contact us.</p>
              <a href="tel:+919256479304" className="course-detail-contact-link">+91 9256479304</a>
              <a href="mailto:khyati@infinyskills.com" className="course-detail-contact-link">khyati@infinyskills.com</a>
            </div>

            {/* {related.length > 0 && (
              <div className="course-detail-related">
                <h3 className="course-detail-related-title">Related courses</h3>
                <ul className="course-detail-related-list">
                  {related.map((c) => (
                    <li key={c.slug}>
                      <Link to={'/course/' + c.slug}>{c.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )} */}

            {/* <div className="course-detail-share">
              <p>Share this course with a friend</p>
              <div className="course-detail-share-icons">
                <a href="#share-fb" aria-label="Facebook">f</a>
                <a href="#share-tw" aria-label="Twitter">X</a>
                <a href="#share-li" aria-label="LinkedIn">in</a>
              </div>
            </div> */}
          </aside>
        </div>
      </div>
    </PageLayout>
  );
};

export default CourseDetail;
